import {
  Alert,
  Anchor,
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Group,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconArrowRight,
  IconCalculator,
  IconFileText,
  IconSchool,
} from "@tabler/icons-react";
import { ReactNode, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getToolBySlug, getToolsBySlugs } from "../../data/toolsCatalog";
import Seo from "../../seo/Seo";
import {
  buildCanonicalUrl,
  createBreadcrumbSchema,
  createFaqSchema,
  createOrganizationSchema,
} from "../../seo/utils";
import "./PageCountCalculatorPages.css";

type NumberValue = number | "";
type SpacingKey = "single" | "onePointFive" | "double";
type FontSizeKey = "11" | "12" | "14";
type PageCalculatorMode = "words-to-pages" | "pages-to-words";

const wordsPerPage: Record<SpacingKey, Record<FontSizeKey, number>> = {
  single: {
    11: 600,
    12: 500,
    14: 400,
  },
  onePointFive: {
    11: 450,
    12: 375,
    14: 300,
  },
  double: {
    11: 300,
    12: 250,
    14: 200,
  },
};

const spacingOptions: Array<{
  value: SpacingKey;
  label: string;
  phrase: string;
}> = [
  { value: "single", label: "Single spacing", phrase: "single spacing" },
  { value: "onePointFive", label: "1.5 spacing", phrase: "1.5 spacing" },
  { value: "double", label: "Double spacing", phrase: "double spacing" },
];

const fontSizeOptions: Array<{ value: FontSizeKey; label: string }> = [
  { value: "11", label: "11 pt" },
  { value: "12", label: "12 pt" },
  { value: "14", label: "14 pt" },
];

const fontStyleOptions = [
  { value: "Arial", label: "Arial" },
  { value: "Times New Roman", label: "Times New Roman" },
  { value: "Calibri", label: "Calibri" },
];

const wordsToPagesFaqs = [
  {
    question: "How many pages is 1000 words?",
    answer:
      "1000 words is usually around 2 pages single-spaced or 4 pages double-spaced using 12 pt font.",
  },
  {
    question: "How many pages is 2000 words?",
    answer:
      "2000 words is usually around 4 pages single-spaced or 8 pages double-spaced using 12 pt font.",
  },
  {
    question: "How many pages is 500 words?",
    answer:
      "500 words is usually around 1 page single-spaced or 2 pages double-spaced using 12 pt font.",
  },
  {
    question: "Does font size affect page count?",
    answer:
      "Yes. Larger font sizes increase the number of pages, while smaller font sizes reduce the number of pages.",
  },
  {
    question: "Does double spacing increase page count?",
    answer:
      "Yes. Double spacing usually makes the same word count take around twice as many pages as single spacing.",
  },
];

const pagesToWordsFaqs = [
  {
    question: "How many words are on one page?",
    answer:
      "One page is usually around 500 words single-spaced or 250 words double-spaced using 12 pt font.",
  },
  {
    question: "How many words are 2 pages?",
    answer:
      "Two pages are usually around 1000 words single-spaced or 500 words double-spaced using 12 pt font.",
  },
  {
    question: "How many words are 5 pages?",
    answer:
      "Five pages are usually around 2500 words single-spaced or 1250 words double-spaced using 12 pt font.",
  },
  {
    question: "How many words are 10 pages?",
    answer:
      "Ten pages are usually around 5000 words single-spaced or 2500 words double-spaced using 12 pt font.",
  },
  {
    question: "Is page count always accurate?",
    answer:
      "No. Page count is an estimate because margins, headings, images, references, tables, and university formatting rules can change the final count.",
  },
];

const wordsToPagesEstimateRows = [
  ["500 words", "1 page", "2 pages"],
  ["1000 words", "2 pages", "4 pages"],
  ["1500 words", "3 pages", "6 pages"],
  ["2000 words", "4 pages", "8 pages"],
  ["2500 words", "5 pages", "10 pages"],
  ["3000 words", "6 pages", "12 pages"],
];

const pagesToWordsEstimateRows = [
  ["1 page", "500 words", "250 words"],
  ["2 pages", "1000 words", "500 words"],
  ["3 pages", "1500 words", "750 words"],
  ["4 pages", "2000 words", "1000 words"],
  ["5 pages", "2500 words", "1250 words"],
  ["10 pages", "5000 words", "2500 words"],
];

const pageConfig = {
  "words-to-pages": {
    slug: "words-to-pages-calculator",
    title: "Words to Pages Calculator",
    seoTitle: "Words to Pages Calculator | How Many Pages Is 1000 Words?",
    metaDescription:
      "Use our free Words to Pages Calculator to find how many pages 500, 1000, 1500, or 2000 words will make. Estimate pages by font size, spacing, and format.",
    description:
      "Use our free Words to Pages Calculator to quickly estimate how many pages your essay, assignment, report, or dissertation will be. Enter your word count, choose spacing, font size, and formatting style, and get an instant page estimate.",
    primaryKeyword: "words to pages calculator",
    secondaryKeywords: [
      "how many pages is 1000 words",
      "how many pages is 2000 words",
      "how many pages is 500 words",
      "how many pages is my essay",
      "word count to pages",
      "essay page calculator",
    ],
    relatedToolSlugs: [
      "pages-to-words-calculator",
      "word-counter",
      "college-essay-editor",
      "speech-word-counter",
    ],
    faqs: wordsToPagesFaqs,
  },
  "pages-to-words": {
    slug: "pages-to-words-calculator",
    title: "Pages to Words Calculator",
    seoTitle: "Pages to Words Calculator | Convert Pages to Word Count",
    metaDescription:
      "Use our free Pages to Words Calculator to estimate how many words are in 1, 2, 5, or 10 pages based on spacing, font size, and academic formatting.",
    description:
      "Use our free Pages to Words Calculator to estimate how many words you need for a specific number of pages. Enter your page count, choose spacing and font size, and instantly calculate the approximate word count for your essay, assignment, or report.",
    primaryKeyword: "pages to words calculator",
    secondaryKeywords: [
      "how many words per page",
      "how many words is 1 page",
      "how many words is 2 pages",
      "how many words is 5 pages",
      "page count to word count",
      "essay word count calculator",
    ],
    relatedToolSlugs: [
      "words-to-pages-calculator",
      "word-counter",
      "college-essay-editor",
      "speech-word-counter",
    ],
    faqs: pagesToWordsFaqs,
  },
};

function toNumberInputValue(value: string | number): NumberValue {
  if (value === "") {
    return "";
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : "";
}

function getSpacingPhrase(spacing: SpacingKey) {
  return spacingOptions.find((option) => option.value === spacing)?.phrase || "";
}

function formatPages(value: number) {
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

function formatWords(value: number) {
  return Math.round(value).toLocaleString("en-US");
}

function PageCountCalculatorPage({ mode }: { mode: PageCalculatorMode }) {
  const config = pageConfig[mode];
  const path = `/tools/${config.slug}`;
  const relatedTools = getToolsBySlugs(config.relatedToolSlugs);
  const tool = getToolBySlug(config.slug);
  const webApplicationSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: config.title,
    applicationCategory: "EducationalApplication",
    operatingSystem: "All",
    url: buildCanonicalUrl(path),
    description:
      mode === "words-to-pages"
        ? "Free calculator to estimate how many pages a word count will make based on spacing and font size."
        : "Free calculator to estimate how many words are in a page count based on spacing and font size.",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
    isAccessibleForFree: true,
  };

  return (
    <Container fluid className="page-count-tool" px={0}>
      <Seo
        title={tool?.seoTitle || config.seoTitle}
        description={tool?.metaDescription || config.metaDescription}
        path={path}
        ogTitle={config.title}
        ogDescription={config.description}
        keywords={[config.primaryKeyword, ...config.secondaryKeywords]}
        schema={[
          createOrganizationSchema(),
          createBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Tools", path: "/tools" },
            { name: config.title, path },
          ]),
          createFaqSchema(config.faqs),
          webApplicationSchema,
        ]}
      />

      <Box className="page-count-hero">
        <Container size="xl">
          <Stack gap="lg">
            <Breadcrumbs separator="/">
              <Anchor component={Link} to="/">
                Home
              </Anchor>
              <Anchor component={Link} to="/tools">
                Tools
              </Anchor>
              <Text>{config.title}</Text>
            </Breadcrumbs>

            <SimpleGrid cols={{ base: 1, lg: 2 }} spacing={34}>
              <Stack gap="md" justify="center">
                <Group gap="sm">
                  <Badge color="green" radius="sm" size="lg">
                    Essay & Writing Tools
                  </Badge>
                  <Badge color="blue" radius="sm" size="lg" variant="light">
                    Free calculator
                  </Badge>
                </Group>
                <ThemeIcon size={58} radius="md" color="green" variant="light">
                  <IconCalculator size={32} />
                </ThemeIcon>
                <Title order={1} className="page-count-title">
                  {config.title}
                </Title>
                <Text size="lg" c="dimmed" className="page-count-intro">
                  {config.description}
                </Text>
              </Stack>

              <Paper
                withBorder
                radius="md"
                p={{ base: "md", md: "xl" }}
                className="page-count-panel"
              >
                <PageCountCalculator mode={mode} />
              </Paper>
            </SimpleGrid>
          </Stack>
        </Container>
      </Box>

      <Container size="xl" py={48}>
        <main className="page-count-content">
          {mode === "words-to-pages" ? (
            <WordsToPagesContent relatedToolSlugs={config.relatedToolSlugs} />
          ) : (
            <PagesToWordsContent relatedToolSlugs={config.relatedToolSlugs} />
          )}

          <RelatedToolCards relatedTools={relatedTools} />

          <FaqSection
            title={
              mode === "words-to-pages"
                ? "FAQs About Words to Pages"
                : "FAQs About Pages to Words"
            }
            faqs={config.faqs}
          />
        </main>
      </Container>
    </Container>
  );
}

function PageCountCalculator({ mode }: { mode: PageCalculatorMode }) {
  const [inputValue, setInputValue] = useState<NumberValue>("");
  const [spacing, setSpacing] = useState<SpacingKey>("double");
  const [fontSize, setFontSize] = useState<FontSizeKey>("12");
  const [fontStyle, setFontStyle] = useState("Times New Roman");
  const numericInput =
    typeof inputValue === "number" && Number.isFinite(inputValue) && inputValue > 0
      ? inputValue
      : undefined;
  const spacingPhrase = getSpacingPhrase(spacing);
  const result = useMemo(() => {
    if (numericInput === undefined) {
      return undefined;
    }

    const perPage = wordsPerPage[spacing][fontSize];

    if (mode === "words-to-pages") {
      return numericInput / perPage;
    }

    return numericInput * perPage;
  }, [fontSize, mode, numericInput, spacing]);

  return (
    <Stack gap="lg" component="section" aria-label="Page count calculator">
      <Stack gap={6}>
        <Title order={2}>
          {mode === "words-to-pages"
            ? "Calculate Pages From Words"
            : "Calculate Words From Pages"}
        </Title>
        <Text c="dimmed">
          Choose common academic formatting settings and get an instant estimate.
        </Text>
      </Stack>

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <NumberInput
          label={mode === "words-to-pages" ? "Word Count" : "Page Count"}
          placeholder={
            mode === "words-to-pages"
              ? "Enter word count, e.g. 1000"
              : "Enter pages, e.g. 5"
          }
          min={0}
          allowNegative={false}
          value={inputValue}
          onChange={(value) => setInputValue(toNumberInputValue(value))}
          aria-label={mode === "words-to-pages" ? "Word count" : "Page count"}
        />
        <Select
          label="Spacing"
          data={spacingOptions.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          value={spacing}
          onChange={(value) => setSpacing((value as SpacingKey) || "double")}
          allowDeselect={false}
          aria-label="Spacing"
        />
        <Select
          label="Font Size"
          data={fontSizeOptions}
          value={fontSize}
          onChange={(value) => setFontSize((value as FontSizeKey) || "12")}
          allowDeselect={false}
          aria-label="Font size"
        />
        <Select
          label="Font Style"
          data={fontStyleOptions}
          value={fontStyle}
          onChange={(value) => setFontStyle(value || "Times New Roman")}
          allowDeselect={false}
          aria-label="Font style"
        />
      </SimpleGrid>

      {numericInput === undefined ? (
        <Alert
          color="yellow"
          variant="light"
          radius="md"
          icon={<IconAlertCircle size={18} />}
        >
          Enter a positive {mode === "words-to-pages" ? "word count" : "page count"} to see the estimate.
        </Alert>
      ) : null}

      {numericInput !== undefined && typeof result === "number" ? (
        <Paper withBorder radius="md" p="lg" className="page-count-result">
          <Stack gap="sm">
            <Title order={2}>
              {mode === "words-to-pages"
                ? "Your Estimated Page Count"
                : "Your Estimated Word Count"}
            </Title>
            {mode === "words-to-pages" ? (
              <Text>
                <strong>{formatWords(numericInput)} words</strong> is approximately{" "}
                <strong>{formatPages(result)} pages</strong> using {fontStyle},{" "}
                {fontSize} pt font and {spacingPhrase}.
              </Text>
            ) : (
              <Text>
                <strong>{formatPages(numericInput)} pages</strong> is approximately{" "}
                <strong>{formatWords(result)} words</strong> using {fontStyle},{" "}
                {fontSize} pt font and {spacingPhrase}.
              </Text>
            )}
            <Text size="sm" c="dimmed">
              This is an estimate. Actual page count may vary depending on
              margins, headings, tables, images, references, and university
              formatting guidelines.
            </Text>
          </Stack>
        </Paper>
      ) : null}
    </Stack>
  );
}

function WordsToPagesContent({
  relatedToolSlugs,
}: {
  relatedToolSlugs: string[];
}) {
  return (
    <>
      <SeoTextSection title="How Many Pages Is 1000 Words?">
        <Text>
          In most academic formats, 1000 words is around 2 pages single-spaced
          or 4 pages double-spaced when using 12 pt font. The exact number of
          pages can change based on font style, spacing, margins, headings, and
          paragraph length.
        </Text>
      </SeoTextSection>

      <SeoTextSection title="How Many Pages Is 2000 Words?">
        <Text>
          2000 words is usually around 4 pages single-spaced or 8 pages
          double-spaced with 12 pt font. This is common for college essays,
          short reports, reflective writing tasks, and coursework submissions.
        </Text>
      </SeoTextSection>

      <SeoTextSection title="How Many Pages Is 500 Words?">
        <Text>
          500 words is approximately 1 page single-spaced or 2 pages
          double-spaced using 12 pt font. Many short essays, discussion posts,
          personal statements, and response papers are around 500 words.
        </Text>
      </SeoTextSection>

      <EstimateTable
        title="Common Words to Pages Estimates"
        headers={["Word Count", "Single-Spaced Pages", "Double-Spaced Pages"]}
        rows={wordsToPagesEstimateRows}
      />

      <SeoTextSection title="Why Students Use a Words to Pages Calculator">
        <Text>
          Students often need to know whether their assignment meets the
          required length before submission. A words to pages calculator helps
          estimate essay length, plan writing time, check assignment
          requirements, and understand how much more content is needed before
          the deadline.
        </Text>
      </SeoTextSection>

      <SupportCta
        title="Need Help Finishing Your Assignment?"
        description="If your word count is ready but your assignment still needs structure, research, editing, or formatting, SubjectBuddy can connect you with academic support experts."
      />

      <InternalLinksSection relatedToolSlugs={relatedToolSlugs} />
    </>
  );
}

function PagesToWordsContent({
  relatedToolSlugs,
}: {
  relatedToolSlugs: string[];
}) {
  return (
    <>
      <SeoTextSection title="How Many Words Are on One Page?">
        <Text>
          One page usually contains around 500 words single-spaced or 250 words
          double-spaced when using 12 pt font. The number can change depending
          on spacing, margins, font style, and formatting requirements.
        </Text>
      </SeoTextSection>

      <SeoTextSection title="How Many Words Are 2 Pages?">
        <Text>
          Two pages usually contain around 1000 words single-spaced or 500 words
          double-spaced using 12 pt font. This is common for short essays,
          response papers, and college assignments.
        </Text>
      </SeoTextSection>

      <SeoTextSection title="How Many Words Are 5 Pages?">
        <Text>
          Five pages usually contain around 2500 words single-spaced or 1250
          words double-spaced using 12 pt font. This length is common for
          detailed essays, reports, and research-based assignments.
        </Text>
      </SeoTextSection>

      <EstimateTable
        title="Common Pages to Words Estimates"
        headers={["Pages", "Single-Spaced Words", "Double-Spaced Words"]}
        rows={pagesToWordsEstimateRows}
      />

      <SeoTextSection title="Why Use a Pages to Words Calculator?">
        <Text>
          A pages to words calculator helps students estimate how much writing
          is needed for an assignment. If your professor asks for a 5-page
          report or a 10-page paper, this tool gives you a quick word count
          estimate so you can plan your research, writing, and editing time
          better.
        </Text>
      </SeoTextSection>

      <SupportCta
        title="Need Help Reaching Your Required Word Count?"
        description="If you know how many words you need but are struggling to complete your assignment, SubjectBuddy can help you plan, structure, edit, and improve your academic work."
      />

      <InternalLinksSection relatedToolSlugs={relatedToolSlugs} />
    </>
  );
}

function SeoTextSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="page-count-section">
      <Stack gap="md">
        <Title order={2}>{title}</Title>
        {children}
      </Stack>
    </section>
  );
}

function EstimateTable({
  title,
  headers,
  rows,
}: {
  title: string;
  headers: string[];
  rows: string[][];
}) {
  return (
    <section className="page-count-section">
      <Stack gap="md">
        <Title order={2}>{title}</Title>
        <Box className="page-count-table-wrap">
          <Table striped highlightOnHover withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                {headers.map((header) => (
                  <Table.Th key={header}>{header}</Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.join("-")}>
                  {row.map((cell) => (
                    <Table.Td key={cell}>{cell}</Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
      </Stack>
    </section>
  );
}

function SupportCta({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <section className="page-count-cta">
      <Group justify="space-between" align="center" gap="lg">
        <Group gap="md" align="flex-start" wrap="nowrap">
          <ThemeIcon radius="md" size={54} color="blue">
            <IconSchool size={28} />
          </ThemeIcon>
          <Stack gap={4}>
            <Title order={2}>{title}</Title>
            <Text c="dimmed" className="page-count-cta-copy">
              {description}
            </Text>
          </Stack>
        </Group>
        <Button
          component={Link}
          to="/assignment-help"
          radius="md"
          rightSection={<IconArrowRight size={18} />}
        >
          Get Assignment Help
        </Button>
      </Group>
    </section>
  );
}

function InternalLinksSection({
  relatedToolSlugs,
}: {
  relatedToolSlugs: string[];
}) {
  const relatedTools = getToolsBySlugs(relatedToolSlugs);
  const linkItems = [
    ...relatedTools.map((tool) => ({
      title: tool.title,
      href: tool.url,
      description: tool.description,
      icon: <IconCalculator size={20} />,
    })),
    {
      title: "Assignment Help",
      href: "/assignment-help",
      description: "Get academic support when your essay needs more than a quick estimate.",
      icon: <IconSchool size={20} />,
    },
    {
      title: "Essay Writing Help",
      href: "/services/essay-editing-help",
      description: "Use the live essay support page for writing, editing, and revision help.",
      icon: <IconFileText size={20} />,
    },
    {
      title: "Dissertation Help",
      href: "/services/dissertation-help-uk",
      description: "Plan longer dissertation chapters after checking page and word estimates.",
      icon: <IconFileText size={20} />,
    },
    {
      title: "How Many Pages Is 1000 Words?",
      href: "/blog/how-many-pages-is-1000-words",
      description: "Read the supporting guide for 1000-word essays and assignments.",
      icon: <IconFileText size={20} />,
    },
    {
      title: "How Many Pages Is 2000 Words?",
      href: "/blog/how-many-pages-is-2000-words",
      description: "Review the detailed 2000-word page count guide.",
      icon: <IconFileText size={20} />,
    },
    {
      title: "How Many Pages Is 500 Words?",
      href: "/blog/how-many-pages-is-500-words",
      description: "Check the short guide for 500-word assignments and responses.",
      icon: <IconFileText size={20} />,
    },
  ];

  // TODO: Add aliases if /essay-writing-help and /dissertation-help return as live routes.
  return (
    <section className="page-count-section">
      <Stack gap="md">
        <Title order={2}>Related Tools and Guides</Title>
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          {linkItems.map((item) => (
            <Paper
              key={item.href}
              component={Link}
              to={item.href}
              withBorder
              radius="md"
              p="lg"
              className="page-count-link-card"
            >
              <Group gap="md" wrap="nowrap" align="flex-start">
                <ThemeIcon color="green" variant="light" radius="md">
                  {item.icon}
                </ThemeIcon>
                <Stack gap={4}>
                  <Text fw={800}>{item.title}</Text>
                  <Text size="sm" c="dimmed">
                    {item.description}
                  </Text>
                </Stack>
              </Group>
            </Paper>
          ))}
        </SimpleGrid>
      </Stack>
    </section>
  );
}

function RelatedToolCards({ relatedTools }: { relatedTools: ReturnType<typeof getToolsBySlugs> }) {
  if (!relatedTools.length) {
    return null;
  }

  return (
    <section className="page-count-section">
      <Stack gap="md">
        <Title order={2}>Related Tools</Title>
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          {relatedTools.map((tool) => (
            <Paper
              key={tool.slug}
              component={Link}
              to={tool.url}
              withBorder
              radius="md"
              p="lg"
              className="page-count-link-card"
            >
              <Group gap="md" wrap="nowrap" align="flex-start">
                <ThemeIcon color="green" variant="light" radius="md">
                  <IconCalculator size={20} />
                </ThemeIcon>
                <Stack gap={4}>
                  <Text fw={800}>{tool.title}</Text>
                  <Text size="sm" c="dimmed">
                    {tool.description}
                  </Text>
                </Stack>
              </Group>
            </Paper>
          ))}
        </SimpleGrid>
      </Stack>
    </section>
  );
}

function FaqSection({
  title,
  faqs,
}: {
  title: string;
  faqs: Array<{ question: string; answer: string }>;
}) {
  return (
    <section className="page-count-section">
      <Stack gap="md">
        <Title order={2}>{title}</Title>
        {faqs.map((faq) => (
          <Paper key={faq.question} withBorder radius="md" p="lg">
            <Stack gap={6}>
              <Title order={3}>{faq.question}</Title>
              <Text c="dimmed" style={{ lineHeight: 1.8 }}>
                {faq.answer}
              </Text>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </section>
  );
}

export function WordsToPagesCalculatorPage() {
  return <PageCountCalculatorPage mode="words-to-pages" />;
}

export function PagesToWordsCalculatorPage() {
  return <PageCountCalculatorPage mode="pages-to-words" />;
}
